// ============================================
// DIRECTORIO.JS — Agenda universal de todos los contactos
// ============================================

import React, { useEffect, useMemo, useRef, useState } from 'react';
import FichaPersona from './components/FichaPersona';
import DetectorDuplicados from './components/DetectorDuplicados';
import LogFusiones from './components/LogFusiones';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE, AREAS_TOMADOR, TIPOS_ROL,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
} from './components/helpersClientes';
import { authHeaders } from './utils/storage';

const FILTROS = [
  { id: 'todos',           label: 'Todos' },
  { id: 'clientes_pf',     label: 'Clientes PF' },
  { id: 'administradores', label: 'Administradores' },
  { id: 'gerentes',        label: 'Gerentes' },
  { id: 'encargados',      label: 'Encargados' },
  { id: 'sin_empresa',     label: 'Sin empresa' },
];

// Normaliza un celular a "+52NNNNNNNNNN" para WhatsApp
function celularWhatsApp(tel) {
  if (!tel) return '';
  const digitos = String(tel).replace(/\D/g, '');
  if (!digitos) return '';
  return digitos.startsWith('52') ? digitos : ('52' + digitos);
}

// Extrae apellido para agrupar por inicial (fallback: primera letra del nombre)
function letraInicial(nombreCompleto) {
  if (!nombreCompleto) return '#';
  const partes = nombreCompleto.trim().split(/\s+/);
  const base = partes.length > 1 ? partes[partes.length - 1] : partes[0];
  const c = base[0] || '#';
  return c.normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase();
}

export default function Directorio({ esCelular }) {
  const [filtro, setFiltro] = useState('todos');
  const [busqueda, setBusqueda] = useState('');
  const [busquedaDebounced, setBusquedaDebounced] = useState('');
  const [personas, setPersonas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [seleccion, setSeleccion] = useState(null);
  const [modal, setModal] = useState(false);
  const [verLog, setVerLog] = useState(false);
  const debRef = useRef(null);

  // Debounce búsqueda 300ms
  useEffect(() => {
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => setBusquedaDebounced(busqueda.trim()), 300);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
  }, [busqueda]);

  const cargar = async () => {
    setCargando(true);
    try {
      const params = new URLSearchParams({ filtro });
      if (busquedaDebounced.length >= 2) params.set('buscar', busquedaDebounced);
      const r = await fetch(`${API_BASE_CLI}/api/clientes/directorio?${params}`, { headers: authHeaders() }).then(x => x.json());
      setPersonas(r.directorio || []);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [filtro, busquedaDebounced]);

  // Agrupa por letra inicial del apellido
  const grupos = useMemo(() => {
    const mapa = new Map();
    personas.forEach(p => {
      const letra = letraInicial(p.nombre_completo);
      if (!mapa.has(letra)) mapa.set(letra, []);
      mapa.get(letra).push(p);
    });
    return [...mapa.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [personas]);

  if (seleccion) {
    return <FichaPersona id={seleccion} onVolver={() => { setSeleccion(null); cargar(); }} esCelular={esCelular} />;
  }
  if (verLog) {
    return <LogFusiones onVolver={() => setVerLog(false)} esCelular={esCelular} />;
  }

  return (
    <div style={{ fontFamily: PP, background: COLOR_FONDO, minHeight: '100%', color: COLOR_GRIS }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: esCelular ? '16px' : '20px 28px', borderBottom: `1px solid ${COLOR_BORDE}`,
        background: '#fff', flexWrap: 'wrap', gap: 12,
      }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: COLOR_OSCURO }}>📇 Directorio</div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
            Agenda universal de contactos · {cargando ? 'Cargando…' : `${personas.length} persona${personas.length === 1 ? '' : 's'}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={estiloBotonSecundario} onClick={() => setVerLog(true)}>⚙️ Historial fusiones</button>
          <button style={estiloBotonPrimario} onClick={() => setModal(true)}>+ Agregar contacto</button>
        </div>
      </div>

      <div style={{
        display: 'flex', gap: 10, flexWrap: 'wrap',
        padding: esCelular ? '12px 16px' : '16px 28px',
        borderBottom: `1px solid ${COLOR_BORDE}`, background: '#fff',
      }}>
        <input
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder="Buscar por nombre, celular, email o empresa…"
          style={{ ...estiloInput, flex: 1, minWidth: 220 }}
        />
      </div>

      <div style={{
        display: 'flex', gap: 6, padding: esCelular ? '10px 16px' : '12px 28px',
        flexWrap: 'wrap', background: '#fff', borderBottom: `1px solid ${COLOR_BORDE}`,
      }}>
        {FILTROS.map(f => (
          <button
            key={f.id}
            onClick={() => setFiltro(f.id)}
            style={{
              padding: '6px 14px', borderRadius: 99, border: 'none',
              background: filtro === f.id ? COLOR_PRINCIPAL : COLOR_FONDO,
              color: filtro === f.id ? '#fff' : COLOR_GRIS,
              fontSize: 12, fontWeight: filtro === f.id ? 600 : 500,
              cursor: 'pointer', fontFamily: PP,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div style={{ padding: esCelular ? 12 : '20px 28px' }}>
        {cargando && <div style={{ textAlign: 'center', padding: 40, color: COLOR_GRIS_CLARO }}>Cargando…</div>}
        {!cargando && personas.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: COLOR_GRIS_CLARO, fontSize: 13 }}>
            {busquedaDebounced || filtro !== 'todos'
              ? 'Sin resultados para los filtros actuales.'
              : 'Aún no hay contactos. Crea el primero con "+ Agregar contacto".'}
          </div>
        )}
        {grupos.map(([letra, lista]) => (
          <div key={letra}>
            <div style={{
              position: 'sticky', top: 0, zIndex: 5,
              background: COLOR_FONDO, padding: '8px 4px',
              fontSize: 11, fontWeight: 700, color: COLOR_OSCURO,
              letterSpacing: '0.05em', borderBottom: `1px solid ${COLOR_BORDE}`,
              marginBottom: 8,
            }}>
              {letra}
            </div>
            <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
              {lista.map(p => (
                <TarjetaContacto key={p.id} persona={p} onAbrir={() => setSeleccion(p.id)} />
              ))}
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <ModalAgregarContacto
          onCerrar={() => setModal(false)}
          onGuardado={(sel) => { setModal(false); cargar(); if (sel) setSeleccion(sel); }}
        />
      )}
    </div>
  );
}

// ============================================
// Tarjeta de contacto con botones rápidos
// ============================================
function TarjetaContacto({ persona, onAbrir }) {
  const celWA = celularWhatsApp(persona.celular);
  const empresa = persona.empresa_principal;
  const etiquetaPuesto = [
    persona.puesto_principal,
    persona.area_principal ? (AREAS_TOMADOR.find(a => a.id === persona.area_principal)?.label) : null,
  ].filter(Boolean).join(' · ');

  return (
    <div style={{
      ...estiloTarjeta, display: 'flex', gap: 14, alignItems: 'flex-start', padding: 14,
    }}>
      <div style={{
        width: 42, height: 42, borderRadius: '50%', background: COLOR_PRINCIPAL,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: '#fff', fontSize: 16, fontWeight: 700, flexShrink: 0,
      }}>
        {persona.nombre_completo ? persona.nombre_completo[0].toUpperCase() : '·'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          onClick={onAbrir}
          style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, cursor: 'pointer' }}
        >
          {persona.nombre_completo}
        </div>
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
          {etiquetaPuesto && <span>{etiquetaPuesto}</span>}
          {etiquetaPuesto && empresa && ' · '}
          {empresa ? <span>{empresa.nombre}</span> : <span style={{ fontStyle: 'italic' }}>Sin empresa</span>}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {(persona.tipos_rol || []).map(t => {
            const d = TIPOS_ROL[t];
            if (!d) return null;
            return (
              <span key={t} style={{
                background: d.color + '20', color: d.color,
                padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>{d.label}</span>
            );
          })}
          {persona.es_cliente_persona_fisica && (
            <span style={{
              background: '#dcde7420', color: '#7a6e00',
              padding: '2px 6px', borderRadius: 4, fontSize: 9, fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '0.04em',
            }}>Cliente PF</span>
          )}
        </div>
        {(persona.celular || persona.email) && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
            {persona.celular && (
              <>
                <a href={`tel:${persona.celular}`} title="Llamar" style={botonIconico}>📞</a>
                {celWA && (
                  <a href={`https://wa.me/${celWA}`} target="_blank" rel="noreferrer" title="WhatsApp" style={botonIconico}>💬</a>
                )}
                <button
                  onClick={() => {
                    try { navigator.clipboard.writeText(persona.celular); }
                    catch { /* no-op */ }
                  }}
                  title="Copiar teléfono"
                  style={botonIconico}
                >📋</button>
              </>
            )}
            {persona.email && (
              <a href={`mailto:${persona.email}`} title="Email" style={botonIconico}>✉️</a>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const botonIconico = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 32, height: 32, borderRadius: 8,
  background: COLOR_FONDO, border: `1px solid ${COLOR_BORDE}`,
  fontSize: 14, cursor: 'pointer', textDecoration: 'none',
};

// ============================================
// Modal + Agregar contacto con detectores inline
// ============================================
function ModalAgregarContacto({ onCerrar, onGuardado }) {
  const [nombre, setNombre] = useState('');
  const [celular, setCelular] = useState('');
  const [email, setEmail] = useState('');
  const [empresaTexto, setEmpresaTexto] = useState('');
  const [empresaSel, setEmpresaSel] = useState(null); // { tipo, id, nombre } | null
  const [puesto, setPuesto] = useState('');
  const [tipoRol, setTipoRol] = useState('');
  const [area, setArea] = useState('');
  const [notas, setNotas] = useState('');
  const [nombreSimilares, setNombreSimilares] = useState(0);
  const [empresaResultados, setEmpresaResultados] = useState([]);
  const [empresaBuscando, setEmpresaBuscando] = useState(false);
  const [duplicadoCelular, setDuplicadoCelular] = useState(null);
  const [ignorarDuplicado, setIgnorarDuplicado] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const timerCelularRef = useRef(null);
  const timerEmpresaRef = useRef(null);

  // Detector de celular duplicado
  useEffect(() => {
    if (timerCelularRef.current) clearTimeout(timerCelularRef.current);
    const digitos = celular.replace(/\D/g, '');
    if (digitos.length < 10) { setDuplicadoCelular(null); return; }
    timerCelularRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API_BASE_CLI}/api/clientes/personas/buscar-por-celular?celular=${encodeURIComponent(celular)}`, { headers: authHeaders() }).then(x => x.json());
        setDuplicadoCelular((r.personas || [])[0] || null);
      } catch { setDuplicadoCelular(null); }
    }, 500);
    return () => { if (timerCelularRef.current) clearTimeout(timerCelularRef.current); };
  }, [celular]);

  // Autocomplete de empresa (busca en grupos Y razones sociales)
  useEffect(() => {
    if (timerEmpresaRef.current) clearTimeout(timerEmpresaRef.current);
    if (empresaSel) return; // ya eligió
    if (empresaTexto.trim().length < 3) { setEmpresaResultados([]); return; }
    timerEmpresaRef.current = setTimeout(async () => {
      setEmpresaBuscando(true);
      try {
        const [g, r] = await Promise.all([
          fetch(`${API_BASE_CLI}/api/clientes/buscar-similares?tipo=grupo&nombre=${encodeURIComponent(empresaTexto)}`, { headers: authHeaders() }).then(x => x.json()),
          fetch(`${API_BASE_CLI}/api/clientes/buscar-similares?tipo=razon_social&nombre=${encodeURIComponent(empresaTexto)}`, { headers: authHeaders() }).then(x => x.json()),
        ]);
        const combinado = [
          ...(g.similares || []).map(x => ({ ...x, tipoEmpresa: 'grupo' })),
          ...(r.similares || []).map(x => ({ ...x, tipoEmpresa: 'razon_social' })),
        ].sort((a, b) => (b.similitud || 0) - (a.similitud || 0)).slice(0, 6);
        setEmpresaResultados(combinado);
      } finally {
        setEmpresaBuscando(false);
      }
    }, 500);
    return () => { if (timerEmpresaRef.current) clearTimeout(timerEmpresaRef.current); };
  }, [empresaTexto, empresaSel]);

  const elegirEmpresa = (item) => {
    setEmpresaSel({ tipo: item.tipoEmpresa, id: item.id, nombre: item.nombre });
    setEmpresaTexto(item.nombre);
    setEmpresaResultados([]);
  };

  const crearEmpresaAlVuelo = async (tipo) => {
    const nombreEmp = empresaTexto.trim();
    if (!nombreEmp) return;
    const url = tipo === 'grupo' ? '/api/clientes/grupos' : '/api/clientes/razones-sociales';
    const body = tipo === 'grupo' ? { nombre: nombreEmp } : { razon_social: nombreEmp };
    const r = await fetch(`${API_BASE_CLI}${url}`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify(body),
    }).then(x => x.json());
    if (r.ok) {
      const entidad = r.grupo || r.razon_social;
      setEmpresaSel({ tipo, id: entidad.id, nombre: tipo === 'grupo' ? entidad.nombre : entidad.razon_social });
      setEmpresaResultados([]);
    }
  };

  const limpiarEmpresa = () => { setEmpresaSel(null); setEmpresaTexto(''); setEmpresaResultados([]); };

  const guardar = async () => {
    setGuardando(true); setError(null);
    try {
      if (!nombre.trim()) throw new Error('Nombre requerido');
      if (duplicadoCelular && !ignorarDuplicado) {
        throw new Error('Confirma que es otra persona o usa el contacto existente');
      }
      // 1. Crear persona
      const p = await fetch(`${API_BASE_CLI}/api/clientes/personas`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({
          nombre_completo: nombre.trim(),
          celular: celular || null,
          email: email || null,
          notas: notas || null,
        }),
      }).then(x => x.json());
      if (!p.ok) throw new Error(p.error || 'Error creando persona');
      const personaId = p.persona.id;

      // 2. Crear rol si hay empresa + tipo
      if (empresaSel && tipoRol) {
        const rol = await fetch(`${API_BASE_CLI}/api/clientes/roles-contacto`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({
            persona_id: personaId,
            tipo: tipoRol,
            area: tipoRol === 'administrador' ? (area || null) : null,
            puesto: puesto || null,
            alcance_tipo: empresaSel.tipo,
            alcance_id: empresaSel.id,
          }),
        }).then(x => x.json());
        if (!rol.ok) throw new Error(rol.error || 'Persona creada pero rol falló');
      }
      onGuardado({ tipo: 'persona', id: personaId });
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.45)', zIndex: 1100,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onCerrar}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 500,
        fontFamily: PP, color: COLOR_GRIS, maxHeight: 'calc(var(--beep-vh) * 92)', overflowY: 'auto',
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 16 }}>
          ➕ Agregar contacto
        </div>

        <div>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Nombre completo *</label>
          <input autoFocus style={estiloInput} value={nombre} onChange={(e) => setNombre(e.target.value)} />
          <DetectorDuplicados
            tipo="persona"
            nombreActual={nombre}
            onSeleccionarExistente={(item) => onGuardado({ tipo: 'persona', id: item.id })}
            onCambioResultados={setNombreSimilares}
          />
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Teléfono celular</label>
          <input style={estiloInput} value={celular} onChange={(e) => { setCelular(e.target.value); setIgnorarDuplicado(false); }} />
          {duplicadoCelular && !ignorarDuplicado && (
            <div style={{
              marginTop: 8, padding: 10, background: '#fff4e5',
              border: '1px solid #f0d9b8', borderRadius: 8,
              fontSize: 12, color: '#a86b1e', lineHeight: 1.5,
            }}>
              ⚠️ Ya existe un contacto con este celular:{' '}
              <strong>{duplicadoCelular.nombre_completo}</strong>
              {duplicadoCelular.empresa && <> · {duplicadoCelular.empresa}</>}
              <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                <button
                  onClick={() => onGuardado({ tipo: 'persona', id: duplicadoCelular.id })}
                  style={{ ...estiloBotonPrimario, padding: '5px 10px', fontSize: 11 }}
                >Ver este contacto</button>
                <button
                  onClick={() => setIgnorarDuplicado(true)}
                  style={{ ...estiloBotonSecundario, padding: '5px 10px', fontSize: 11 }}
                >Sí, es otra persona</button>
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Email</label>
          <input style={estiloInput} value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Empresa (opcional)</label>
          {empresaSel ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: 10,
              background: '#eaf2fb', borderRadius: 8, marginTop: 6,
            }}>
              <span style={{ fontSize: 14 }}>🏢</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>{empresaSel.nombre}</div>
                <div style={{ fontSize: 10, color: COLOR_GRIS_CLARO }}>
                  {empresaSel.tipo === 'grupo' ? 'Grupo (cliente)' : 'Razón social'}
                </div>
              </div>
              <button
                onClick={limpiarEmpresa}
                style={{ background: 'transparent', border: 'none', fontSize: 14, cursor: 'pointer', color: COLOR_GRIS_CLARO }}
              >✕</button>
            </div>
          ) : (
            <>
              <input
                style={estiloInput}
                value={empresaTexto}
                onChange={(e) => setEmpresaTexto(e.target.value)}
                placeholder="Buscar o escribir…"
              />
              {(empresaResultados.length > 0 || (empresaTexto.trim().length >= 3 && !empresaBuscando)) && (
                <div style={{
                  marginTop: 4, border: `1px solid ${COLOR_BORDE}`, borderRadius: 8,
                  background: '#fff', maxHeight: 200, overflowY: 'auto',
                }}>
                  {empresaResultados.map((e) => (
                    <div
                      key={`${e.tipoEmpresa}-${e.id}`}
                      onClick={() => elegirEmpresa(e)}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${COLOR_BORDE}`,
                      }}
                    >
                      <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>
                        🏢 {e.nombre} <span style={{ color: COLOR_GRIS_CLARO, fontWeight: 400, fontSize: 11 }}>
                          ({e.tipoEmpresa === 'grupo' ? 'Grupo (cliente)' : 'Razón social'})
                        </span>
                      </div>
                      {e.contexto && <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>{e.contexto}</div>}
                    </div>
                  ))}
                  {empresaTexto.trim().length >= 3 && (
                    <div style={{ padding: '8px 12px', borderTop: `1px solid ${COLOR_BORDE}` }}>
                      <button
                        onClick={() => crearEmpresaAlVuelo('grupo')}
                        style={{ ...estiloBotonSecundario, marginRight: 6, fontSize: 11 }}
                      >+ Crear grupo (cliente) "{empresaTexto.trim()}"</button>
                      <button
                        onClick={() => crearEmpresaAlVuelo('razon_social')}
                        style={{ ...estiloBotonSecundario, fontSize: 11 }}
                      >+ Crear razón social "{empresaTexto.trim()}"</button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {empresaSel && (
          <>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Puesto (opcional)</label>
              <input style={estiloInput} value={puesto} onChange={(e) => setPuesto(e.target.value)} />
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Tipo de rol (opcional)</label>
              <select style={estiloInput} value={tipoRol} onChange={(e) => setTipoRol(e.target.value)}>
                <option value="">Contacto general (sin rol específico)</option>
                <option value="administrador">Administrador</option>
                <option value="gerente">Gerente</option>
                <option value="encargado_sucursal">Encargado de sucursal</option>
              </select>
            </div>
            {tipoRol === 'administrador' && (
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600 }}>Área</label>
                <select style={estiloInput} value={area} onChange={(e) => setArea(e.target.value)}>
                  <option value="">— Sin especificar —</option>
                  {AREAS_TOMADOR.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
              </div>
            )}
          </>
        )}

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Notas</label>
          <textarea
            style={{ ...estiloInput, minHeight: 60, resize: 'vertical' }}
            value={notas} onChange={(e) => setNotas(e.target.value)}
          />
        </div>

        {error && <div style={{ color: '#e05c5c', fontSize: 12, marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button style={estiloBotonSecundario} onClick={onCerrar}>Cancelar</button>
          <button style={estiloBotonPrimario} onClick={guardar} disabled={guardando || !nombre.trim()}>
            {guardando
              ? 'Guardando…'
              : (nombreSimilares > 0 ? 'Sí, crear nuevo contacto' : 'Guardar contacto')}
          </button>
        </div>
      </div>
    </div>
  );
}
